import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  LS_IS_JUST_SIGNED_IN_NAME,
  LS_IS_JUST_SIGNED_IN_VALUE,
  useCognitoCheckUser,
} from '@/entities/user/currentUser';
import { setAuthForm, useViewerStore } from '@/entities/viewer';

import { AUTH_API } from '@/shared/api/amplify';
import { PATHS } from '@/shared/constants/routes/paths';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { AuthForm } from '@/shared/types/Cognito';
import { PasswordInput } from '@/shared/ui/form/PasswordInput';
import { ControlledTextField } from '@/shared/ui/form/TextField';

import { FormContainer } from '../../components/FormContainer';
import { GoBackLink } from '../../components/GoBackLink';
import { SubmitButton } from '../../components/SubmitButton/styled';
import { DATA_TEST_IDS } from '../../lib/constants';
import { TexFieldContainer } from './styled';

type FormValues = {
  code: string;
  password: string;
};

export const ChangePassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { refetch: refetchCognitoUser } = useCognitoCheckUser({ enabled: false });

  const email = useViewerStore((state) => state.email) || '';

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      code: '',
      password: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (input: FormValues) => {
    try {
      setIsLoading(true);

      const { code, password } = input;

      await AUTH_API.forgotPasswordSubmit(email, code, password);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await AUTH_API.signIn(email, password);

      if (response) {
        await refetchCognitoUser();
        localStorage.setItem(LS_IS_JUST_SIGNED_IN_NAME, LS_IS_JUST_SIGNED_IN_VALUE);
        navigate(PATHS.homepage);
      }

      reset();
    } catch (e) {
      showErrorAlert(e);
    }
    setIsLoading(false);
  };

  const switchToSigninForm = () => {
    setAuthForm(AuthForm.Signin);
  };

  return (
    <FormContainer title="Change password" subtitle="Set new password" onSubmit={handleSubmit(onSubmit)}>
      <>
        <TexFieldContainer>
          <ControlledTextField
            label="Verification code"
            slotProps={{
              input: {
                inputProps: {
                  autoComplete: 'one-time-code',
                },
              },
            }}
            control={control}
            disabled={isLoading}
            fieldName="code"
            placeholder="Code"
            showEndAdornmentOnError={false}
            variant="outlined"
          />
          <PasswordInput
            label="New Password"
            control={control}
            isDisabled={isLoading}
            placeholder="Enter new password"
          />
        </TexFieldContainer>

        <SubmitButton
          data-testid="auth-page-sign-in"
          disabled={isLoading}
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Sign in
        </SubmitButton>

        <GoBackLink data-testid={DATA_TEST_IDS.signInButton} text="Back to Sign in" onClick={switchToSigninForm} />
      </>
    </FormContainer>
  );
};
